import { useEffect, useCallback, useRef, useState } from 'react';
import { getBattlePairsWSClient, PairEventType } from '../api/battlePairsWebSocket';

/**
 * React хук для работы с WebSocket уведомлениями Battle Pairs
 * 
 * @param {Object} params
 * @param {string|null} params.roundId - UUID раунда для подписки (если null, подписка не создаётся)
 * @param {Function} params.onEvent - Обработчик событий
 * @param {boolean} [params.autoConnect=true] - Автоматически подключаться при монтировании
 * @param {string[]} [params.eventTypes] - Фильтр типов событий (если не указан, все события)
 * 
 * @returns {Object}
 * @returns {boolean} return.connected - Статус подключения
 * @returns {Function} return.subscribe - Подписаться на раунд
 * @returns {Function} return.unsubscribe - Отписаться от раунда
 * @returns {Function} return.disconnect - Отключиться от сервера
 * @returns {Object|null} return.lastEvent - Последнее полученное событие
 * @returns {Error|null} return.error - Ошибка подключения
 * 
 * @example
 * function BattleRoundPage({ roundId }) {
 *   const { connected, lastEvent, error } = useBattlePairsWebSocket({
 *     roundId,
 *     onEvent: (event) => {
 *       console.log('Событие пары:', event);
 *     }
 *   });
 * 
 *   return (
 *     <div>
 *       {connected ? 'Подключено' : 'Отключено'}
 *       {error && <div>Ошибка: {error.message}</div>}
 *       {lastEvent && <div>Последнее событие: {lastEvent.eventType}</div>}
 *     </div>
 *   );
 * }
 */
export const useBattlePairsWebSocket = ({
  roundId = null,
  onEvent,
  autoConnect = true,
  eventTypes = null
}) => {
  const wsClientRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const [error, setError] = useState(null);
  const onEventRef = useRef(onEvent);

  // Обновляем ref на callback, чтобы избежать проблем со stale closure
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  // Фильтрация событий по типам
  const handleEvent = useCallback((event) => {
    if (eventTypes && !eventTypes.includes(event.eventType)) {
      return;
    }
    setLastEvent(event);
    if (onEventRef.current) {
      onEventRef.current(event);
    }
  }, [eventTypes]);

  // Подключение и подписка
  useEffect(() => {
    // Получаем singleton клиент
    wsClientRef.current = getBattlePairsWSClient();

    const initConnection = async () => {
      try {
        await wsClientRef.current.connect();
        setConnected(true);
        setError(null);
      } catch (err) {
        console.error('[useBattlePairsWebSocket] Connection error:', err);
        setError(err);
        setConnected(false);
      }
    };

    if (autoConnect) {
      initConnection();
    }

    // Подписка на раунд
    if (roundId && wsClientRef.current.isConnected()) {
      unsubscribeRef.current = wsClientRef.current.subscribeToRound(
        roundId,
        handleEvent
      );
    }

    // Отписка и отключение при размонтировании
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      // Не отключаем клиент полностью, т.к. он singleton и может использоваться в других компонентах
      setConnected(wsClientRef.current?.isConnected() ?? false);
    };
  }, [roundId, autoConnect, handleEvent]);

  // Функции управления
  const subscribe = useCallback((newRoundId) => {
    if (wsClientRef.current && wsClientRef.current.isConnected()) {
      // Отписываемся от старого раунда
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      // Подписываемся на новый
      unsubscribeRef.current = wsClientRef.current.subscribeToRound(
        newRoundId,
        handleEvent
      );
    }
  }, [handleEvent]);

  const unsubscribe = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
  }, []);

  const disconnect = useCallback(() => {
    if (wsClientRef.current) {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      wsClientRef.current.disconnect();
      setConnected(false);
    }
  }, []);

  return {
    connected,
    subscribe,
    unsubscribe,
    disconnect,
    lastEvent,
    error
  };
};

/**
 * Утилита для форматирования сообщения события
 * @param {Object} event - Событие Battle Pairs
 * @returns {string}
 */
export const formatPairEventMessage = (event) => {
  const messages = {
    [PairEventType.PAIR_CREATED]: `Создана пара #${event.pairNumber}`,
    [PairEventType.PAIR_UPDATED]: `Обновлена пара #${event.pairNumber}`,
    [PairEventType.PAIR_DELETED]: `Удалена пара #${event.pairNumber}`,
    [PairEventType.PAIR_ACTIVATED]: `Пара #${event.pairNumber} активирована`,
    [PairEventType.PAIR_FINISHED]: `Пара #${event.pairNumber} завершена`,
    [PairEventType.PAIR_DISQUALIFIED]: `Пара #${event.pairNumber} дисквалифицирована`,
    [PairEventType.WINNER_ANNOUNCED]: `Победитель пары #${event.pairNumber}: ${event.winner?.name || 'Неизвестно'}`,
    [PairEventType.DRAW_COMPLETED]: `Жеребьёвка завершена! Сформировано ${event.totalPairs} пар`,
    [PairEventType.TRACK_UPLOADED]: `Новый трек в паре #${event.pairNumber}: ${event.message}`
  };

  return messages[event.eventType] || event.message || 'Событие пары';
};

export default {
  useBattlePairsWebSocket,
  formatPairEventMessage,
  PairEventType
};
